// Laporan Keuangan — Tab Potongan PPh (2026-02-13, user request).
// 16-PPH-EVIDENCE-R1 (2026-06-14, user request): workflow bukti pembayaran
// lengkap — pembayaran satu/beberapa invoice dalam satu setoran (NTPN/billing),
// status 3 tingkat (Belum Dibayar / Dibayar Sebagian / Sudah Dibayar), bukti
// & riwayat bisa dibuka kembali, data lama tanpa bukti → "Bukti belum
// dilengkapi". Nilai invoice & pembukuan akuntansi terpisah TIDAK diubah.
import { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Search, Landmark, History } from "lucide-react";
import api, { formatDetail } from "@/lib/api";
import { fmtDate, formatIDR } from "@/lib/constants";
import { useAuth } from "@/lib/auth";
import { useAccess } from "@/lib/access";
import PphPaymentDialog from "./PphPaymentDialog";
import PphPaymentHistoryDialog from "./PphPaymentHistoryDialog";

// 2026-10-14: label opsi Sub Project dengan disambiguasi WO/VO.
function subOptionLabel(p) {
  if (p.project_type === "working_order") return `WO · ${p.wo_number || p.project_name || "-"}`;
  if (p.project_type === "variation_order") return `VO · ${p.variation_order_name || p.project_name || "-"}`;
  return p.project_name || "-";
}

const STATUS_META = {
  unpaid: { label: "Belum Dibayar", cls: "bg-amber-50 text-amber-700 border-amber-200" },
  partial: { label: "Dibayar Sebagian", cls: "bg-sky-50 text-sky-700 border-sky-200" },
  paid: { label: "Sudah Dibayar", cls: "bg-emerald-50 text-emerald-700 border-emerald-200" },
};

export default function PphWithholdingTab({ projects = [] }) {
  const { user } = useAuth();
  const { canPerm } = useAccess();
  const canManage = !!user && canPerm(user, "finance_report", "update");
  const [items, setItems] = useState([]);
  const [summary, setSummary] = useState(null);
  const [banks, setBanks] = useState([]);
  const [methods, setMethods] = useState(["Transfer Bank", "E-Billing", "Setor Tunai", "Lainnya"]);
  const [loading, setLoading] = useState(false);
  const [filter, setFilter] = useState("all"); // all | unpaid | partial | paid
  const [q, setQ] = useState("");
  // 2026-10-14 (user request): filter Induk Project → Sub Project (pola Invoice & Piutang).
  const [parentId, setParentId] = useState("all");
  const [subId, setSubId] = useState("all");
  const [selected, setSelected] = useState(() => new Set());
  const [payDialog, setPayDialog] = useState(null);   // { items } | { payment } (edit)
  const [historyFor, setHistoryFor] = useState(null); // item
  const [editingPayment, setEditingPayment] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/finance/pph-withholding");
      setItems(Array.isArray(data?.items) ? data.items : []);
      setSummary(data?.summary || null);
    } catch (err) {
      toast.error(formatDetail(err?.response?.data?.detail) || "Gagal memuat data potongan PPh.");
    } finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    api.get("/finance/pph-payments/bank-options")
      .then(({ data }) => {
        setBanks(Array.isArray(data?.items) ? data.items : []);
        if (Array.isArray(data?.methods) && data.methods.length) setMethods(data.methods);
      })
      .catch(() => {});
  }, []);

  const projById = useMemo(() => new Map((projects || []).map((p) => [p.id, p])), [projects]);
  const parentOpts = useMemo(() => {
    const ids = new Set((projects || []).map((p) => p.id));
    return (projects || []).filter((p) => !p.parent_project_id || !ids.has(p.parent_project_id));
  }, [projects]);
  const subOpts = useMemo(() => (
    parentId === "all" ? [] : (projects || []).filter((p) => p.parent_project_id === parentId)
  ), [projects, parentId]);

  const filtered = useMemo(() => {
    let list = items;
    if (filter !== "all") list = list.filter((i) => i.payment_status === filter);
    if (subId !== "all") list = list.filter((i) => i.project_id === subId);
    else if (parentId !== "all") {
      list = list.filter((i) => (projById.get(i.project_id)?.parent_project_id || i.project_id) === parentId);
    }
    const query = q.trim().toLowerCase();
    if (query) {
      list = list.filter((i) =>
        (i.invoice_no || "").toLowerCase().includes(query)
        || (i.project_name || "").toLowerCase().includes(query)
        || (i.client || "").toLowerCase().includes(query));
    }
    return list;
  }, [items, filter, q, parentId, subId, projById]);

  const selectable = (i) => i.payment_status !== "paid";
  const keyOf = (i) => `${i.project_id}|${i.entry_id}`;
  const toggleSelect = (i) => setSelected((prev) => {
    const next = new Set(prev);
    if (next.has(keyOf(i))) next.delete(keyOf(i)); else next.add(keyOf(i));
    return next;
  });
  const selectedItems = useMemo(
    () => items.filter((i) => selected.has(keyOf(i)) && selectable(i)),
    [items, selected],
  );

  return (
    <div className="space-y-3" data-testid="pph-tab">
      <div className="border border-slate-200 bg-white p-3 flex items-start gap-2">
        <Landmark size={15} className="text-[#0B1D3A] mt-0.5 shrink-0" />
        <p className="text-[11px] text-slate-600 leading-relaxed" data-testid="pph-explainer">
          Potongan PPh atas invoice <b>bukan uang masuk perusahaan</b> — client memotongnya dan menyetorkannya ke negara.
          Tab ini melacak penyetoran ke negara lengkap dengan <b>NTPN / bukti setor / kode billing / bukti upload</b>.
          Satu pembayaran pajak dapat mencakup beberapa invoice sekaligus. Pencatatan ini <b>tidak mengubah</b> nilai invoice maupun pembukuan di software akuntansi.
        </p>
      </div>

      {summary && (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2" data-testid="pph-summary">
          <div className="border border-slate-200 bg-white p-3">
            <div className="text-[9px] uppercase tracking-wider text-slate-500 font-semibold">Total Potongan PPh</div>
            <div className="font-display text-lg font-bold text-[#0B1D3A] tabular-nums" data-testid="pph-total">{formatIDR(summary.total_pph)}</div>
            <div className="text-[10px] text-slate-500">{summary.paid_count + summary.partial_count + summary.unpaid_count} invoice</div>
          </div>
          <div className="border border-emerald-200 bg-emerald-50/50 p-3">
            <div className="text-[9px] uppercase tracking-wider text-emerald-700 font-semibold">Sudah Dibayar</div>
            <div className="font-display text-lg font-bold text-emerald-800 tabular-nums" data-testid="pph-paid-total">{formatIDR(summary.paid_total)}</div>
            <div className="text-[10px] text-emerald-700">{summary.paid_count} invoice</div>
          </div>
          <div className="border border-sky-200 bg-sky-50/50 p-3">
            <div className="text-[9px] uppercase tracking-wider text-sky-700 font-semibold">Dibayar Sebagian</div>
            <div className="font-display text-lg font-bold text-sky-800 tabular-nums" data-testid="pph-partial-total">{formatIDR(summary.partial_total ?? 0)}</div>
            <div className="text-[10px] text-sky-700">{summary.partial_count ?? 0} invoice</div>
          </div>
          <div className="border border-amber-200 bg-amber-50/50 p-3">
            <div className="text-[9px] uppercase tracking-wider text-amber-700 font-semibold">Sisa Belum Dibayar</div>
            <div className="font-display text-lg font-bold text-amber-800 tabular-nums" data-testid="pph-unpaid-total">{formatIDR(summary.unpaid_total)}</div>
            <div className="text-[10px] text-amber-700">{summary.unpaid_count} invoice</div>
          </div>
        </div>
      )}

      <div className="flex items-center gap-2 flex-wrap">
        <div className="flex items-center gap-1" data-testid="pph-filters">
          {[["all", "Semua"], ["unpaid", "Belum Dibayar"], ["partial", "Dibayar Sebagian"], ["paid", "Sudah Dibayar"]].map(([k, label]) => (
            <button key={k} type="button" onClick={() => setFilter(k)} data-testid={`pph-filter-${k}`}
              className={`px-3 py-1.5 text-[10px] uppercase tracking-wider font-semibold border transition-colors ${filter === k ? "bg-[#0B1D3A] text-white border-[#0B1D3A]" : "bg-white text-slate-600 border-slate-300 hover:text-[#0B1D3A]"}`}>
              {label}
            </button>
          ))}
        </div>
        {/* 2026-10-14 (user request): filter Induk → Sub Project. */}
        <select value={parentId} onChange={(e) => { setParentId(e.target.value); setSubId("all"); }}
          data-testid="pph-parent-filter"
          className="h-9 px-2 border border-slate-300 text-[11px] bg-white text-slate-700 focus:outline-none focus:border-[#0B1D3A] max-w-[220px]">
          <option value="all">Semua Induk Project</option>
          {parentOpts.map((p) => <option key={p.id} value={p.id}>{p.project_name || "-"}</option>)}
        </select>
        <select value={subId} onChange={(e) => setSubId(e.target.value)} disabled={parentId === "all"}
          data-testid="pph-sub-filter"
          className="h-9 px-2 border border-slate-300 text-[11px] bg-white text-slate-700 focus:outline-none focus:border-[#0B1D3A] max-w-[220px] disabled:bg-slate-100 disabled:text-slate-400 disabled:cursor-not-allowed">
          <option value="all">{parentId === "all" ? "Pilih Induk dulu" : "Semua Sub Project"}</option>
          {subOpts.map((p) => <option key={p.id} value={p.id}>{subOptionLabel(p)}</option>)}
        </select>
        {canManage && (
          <button type="button" disabled={selectedItems.length === 0}
            onClick={() => setPayDialog({ items: selectedItems })}
            data-testid="pph-bulk-pay"
            className="px-3 py-1.5 text-[10px] uppercase tracking-wider font-semibold bg-emerald-600 text-white hover:bg-emerald-700 disabled:opacity-40">
            Bayar PPh ({selectedItems.length} invoice)
          </button>
        )}
        <div className="relative ml-auto w-full sm:w-72">
          <Search size={13} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" />
          <input value={q} onChange={(e) => setQ(e.target.value)}
            placeholder="Cari no invoice / proyek / client…" data-testid="pph-search"
            className="w-full h-9 border border-slate-300 pl-8 pr-2 text-[11px] bg-white focus:outline-none focus:border-[#0B1D3A]" />
        </div>
      </div>

      <div className="border border-slate-200 bg-white overflow-x-auto">
        {loading ? (
          <div className="p-6 text-center text-[11px] italic text-slate-400">Memuat…</div>
        ) : filtered.length === 0 ? (
          <div className="p-8 text-center text-[11px] text-slate-400" data-testid="pph-empty">
            {items.length === 0 ? "Belum ada invoice dengan potongan PPh." : "Tidak ada data pada filter ini."}
          </div>
        ) : (
          <table className="w-full text-[11px]" data-testid="pph-table">
            <thead className="bg-slate-50 border-b border-slate-200 text-[9px] uppercase tracking-wider text-slate-600">
              <tr>
                <th className="px-3 py-2 text-center w-10">Pilih</th>
                <th className="px-3 py-2 text-left">Tgl Invoice</th>
                <th className="px-3 py-2 text-left">No Invoice</th>
                <th className="px-3 py-2 text-left">Proyek</th>
                <th className="px-3 py-2 text-left">Client</th>
                <th className="px-3 py-2 text-right">Nilai Invoice</th>
                <th className="px-3 py-2 text-right">Potongan PPh</th>
                <th className="px-3 py-2 text-right">Sisa Terutang</th>
                <th className="px-3 py-2 text-left">Status</th>
                <th className="px-3 py-2 text-left">Aksi</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((i) => {
                const sm = STATUS_META[i.payment_status] || STATUS_META.unpaid;
                return (
                  <tr key={i.entry_id} className="border-b border-slate-100 hover:bg-slate-50" data-testid={`pph-row-${i.entry_id}`}>
                    <td className="px-3 py-2 text-center">
                      {selectable(i) ? (
                        <input type="checkbox" checked={selected.has(keyOf(i))} onChange={() => toggleSelect(i)}
                          data-testid={`pph-select-${i.entry_id}`} />
                      ) : <span className="text-slate-300">—</span>}
                    </td>
                    <td className="px-3 py-2 tabular-nums whitespace-nowrap text-slate-700">{i.invoice_date ? fmtDate(i.invoice_date) : "-"}</td>
                    <td className="px-3 py-2 font-semibold text-slate-800 whitespace-nowrap">{i.invoice_no || <span className="text-slate-400 italic font-normal">Belum ada nomor invoice</span>}</td>
                    <td className="px-3 py-2 text-slate-700 max-w-[220px] truncate" title={i.project_name}>{i.project_name || "-"}</td>
                    <td className="px-3 py-2 text-slate-600 max-w-[160px] truncate" title={i.client}>{i.client || "-"}</td>
                    <td className="px-3 py-2 text-right tabular-nums text-slate-700">{formatIDR(i.gross_amount)}</td>
                    <td className="px-3 py-2 text-right tabular-nums font-bold text-[#0B1D3A] whitespace-nowrap">
                      {formatIDR(i.pph_amount)}
                      {i.pph_pct ? <span className="text-[9px] text-slate-400 font-normal"> ({i.pph_pct}%)</span> : null}
                    </td>
                    <td className="px-3 py-2 text-right tabular-nums text-slate-700" data-testid={`pph-remaining-${i.entry_id}`}>{formatIDR(i.pph_remaining)}</td>
                    <td className="px-3 py-2" data-testid={`pph-status-${i.entry_id}`}>
                      <span className={`inline-block px-1.5 py-0.5 text-[9px] uppercase font-semibold border ${sm.cls}`}>
                        {sm.label}{i.payment_status === "partial" ? ` (${formatIDR(i.pph_paid_amount)})` : ""}
                      </span>
                      {i.evidence_missing && (
                        <span className="ml-1 inline-block px-1.5 py-0.5 text-[9px] uppercase font-semibold border bg-slate-50 text-slate-500 border-slate-300" data-testid={`pph-evidence-missing-${i.entry_id}`}>
                          Bukti belum dilengkapi
                        </span>
                      )}
                      {i.marked_by_name && i.payments_count === 0 && <div className="text-[9px] text-slate-400 mt-0.5">oleh {i.marked_by_name}</div>}
                    </td>
                    <td className="px-3 py-2">
                      <div className="flex items-center gap-1.5">
                        {canManage && selectable(i) && (
                          <button type="button" onClick={() => setPayDialog({ items: [i] })}
                            data-testid={`pph-pay-${i.entry_id}`}
                            className="px-2 py-1 text-[9px] uppercase font-semibold bg-emerald-600 text-white hover:bg-emerald-700">
                            Bayar
                          </button>
                        )}
                        <button type="button" onClick={() => setHistoryFor(i)}
                          data-testid={`pph-history-${i.entry_id}`}
                          className="inline-flex items-center gap-1 px-2 py-1 text-[9px] uppercase font-semibold border border-slate-300 text-slate-600 hover:bg-slate-50">
                          <History size={10} /> Riwayat{i.payments_count ? ` (${i.payments_count})` : ""}
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      {payDialog && (
        <PphPaymentDialog
          items={payDialog.items}
          banks={banks}
          methods={methods}
          payment={payDialog.payment || null}
          onClose={() => setPayDialog(null)}
          onSaved={() => { setPayDialog(null); setSelected(new Set()); load(); }}
        />
      )}
      {historyFor && (
        <PphPaymentHistoryDialog
          item={historyFor}
          canManage={canManage}
          onClose={() => setHistoryFor(null)}
          onEdit={(p) => {
            setHistoryFor(null);
            const withRemaining = (p.allocations || []).map((a) => ({
              ...a,
              pph_remaining: (items.find((x) => x.entry_id === a.entry_id)?.pph_remaining ?? 0) + (a.amount || 0),
            }));
            setPayDialog({ payment: { ...p, allocations: withRemaining }, items: [] });
          }}
        />
      )}
    </div>
  );
}
